import type {
  TamCalculatorFilters,
  TamCalculatorFilterOptions,
} from "~/react/types/tam_calculator"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { AcvFilter } from "~/react/components/tam_calculator_filters/acv_filter"
import { LocationFilter } from "~/react/components/discover_filters/location_filter"
import { IndustryFilter } from "~/react/components/discover_filters/industry_filter"
import { CompanySizeFilter } from "~/react/components/discover_filters/company_size_filter"
import { CompanyTypeFilter } from "~/react/components/discover_filters/company_type_filter"
import { KeywordsFilter } from "~/react/components/discover_filters/keywords_filter"

const t = (key: string) => window.i18n.t(key)

// Helper to ensure value is always an array
function ensureArray(value: unknown): string[] {
  if (value === undefined || value === null) return []
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (Array.isArray(item)) return String(item[0])
      return String(item)
    })
  }
  if (typeof value === "object") return Object.keys(value)
  return [String(value)]
}

interface TamCalculatorFiltersProps {
  filters: TamCalculatorFilters
  filterOptions: TamCalculatorFilterOptions
  updateFilter: <K extends keyof TamCalculatorFilters>(
    key: K,
    value: TamCalculatorFilters[K]
  ) => void
  clearAllFilters: () => void
  hasFilters: boolean
  onFiltersChange: (filters: TamCalculatorFilters) => void
}

export default function TamCalculatorFilters({
  filters,
  filterOptions,
  updateFilter,
  clearAllFilters,
  hasFilters,
  onFiltersChange,
}: TamCalculatorFiltersProps) {
  const handleAcvChange = (value: number | null) => {
    updateFilter("acv", value || undefined)
    // Trigger results fetch when ACV changes (if we have company filters)
    const hasCompanyFilters =
      filters.location_country_included?.length ||
      filters.industry_included?.length ||
      filters.company_size?.length ||
      filters.company_type_included?.length ||
      filters.keywords_included?.length

    if (hasCompanyFilters) {
      onFiltersChange({ ...filters, acv: value || undefined })
    }
  }

  return (
    <div className="tam-calculator__filters">
      {/* Step 1: Business Assumption */}
      <div className="tam-calculator__calculation-basis">
        <div className="tam-calculator__filters-header mb-3">
          <div className="tam-calculator__step-badge">1</div>
          <strong className="tam-calculator__filters-title">
            {t("js.visitors.tam_calculator.calculation_basis.title")}
          </strong>
        </div>
        <div className="tam-calculator__calculation-basis-content">
          <AcvFilter value={filters.acv || null} onChange={handleAcvChange} />
        </div>
      </div>

      {/* Step 2: Define Your Target */}
      <div className="tam-calculator__filters-section">
        <div className="tam-calculator__filters-header">
          <div className="tam-calculator__filters-header-title">
            <div className="tam-calculator__step-badge">2</div>
            <strong className="tam-calculator__filters-title">
              {t("js.visitors.tam_calculator.filters.title")}
            </strong>
          </div>
          {hasFilters && (
            <Button
              size="xs"
              onClick={clearAllFilters}
              className="tam-calculator__filters-clear-all"
            >
              {t("js.visitors.tam_calculator.filters.clear_all")}
            </Button>
          )}
        </div>

        <div className="tam-calculator__filters-list">

          {/* Location Filter - Country only */}
          <LocationFilter
            includedContinentValues={[]}
            excludedContinentValues={[]}
            includedBusinessRegionValues={[]}
            excludedBusinessRegionValues={[]}
            includedCountryValues={ensureArray(filters.location_country_included)}
            excludedCountryValues={ensureArray(filters.location_country_excluded)}
            includedStateValues={[]}
            excludedStateValues={[]}
            includedCityValues={[]}
            excludedCityValues={[]}
            onChangeIncludedContinent={() => {}}
            onChangeExcludedContinent={() => {}}
            onChangeIncludedBusinessRegion={() => {}}
            onChangeExcludedBusinessRegion={() => {}}
            onChangeIncludedCountry={(value) => {
              // If clearing (empty array), clear both included and excluded
              if (value.length === 0) {
                updateFilter("location_country_included", undefined)
                updateFilter("location_country_excluded", undefined)
                onFiltersChange({
                  ...filters,
                  location_country_included: undefined,
                  location_country_excluded: undefined,
                })
              } else {
                // Remove included values from excluded list
                const currentExcluded = ensureArray(filters.location_country_excluded)
                const newExcluded = currentExcluded.filter((v) => !value.includes(v))
                updateFilter("location_country_excluded", newExcluded.length > 0 ? newExcluded : undefined)
                updateFilter("location_country_included", value)
                onFiltersChange({
                  ...filters,
                  location_country_included: value,
                  location_country_excluded: newExcluded.length > 0 ? newExcluded : undefined,
                })
              }
            }}
            onChangeExcludedCountry={(value) => {
              // If clearing (empty array), clear both included and excluded
              if (value.length === 0) {
                updateFilter("location_country_included", undefined)
                updateFilter("location_country_excluded", undefined)
                onFiltersChange({
                  ...filters,
                  location_country_included: undefined,
                  location_country_excluded: undefined,
                })
              } else {
                // Remove excluded values from included list
                const currentIncluded = ensureArray(filters.location_country_included)
                const newIncluded = currentIncluded.filter((v) => !value.includes(v))
                updateFilter("location_country_included", newIncluded.length > 0 ? newIncluded : undefined)
                updateFilter("location_country_excluded", value)
                onFiltersChange({
                  ...filters,
                  location_country_included: newIncluded.length > 0 ? newIncluded : undefined,
                  location_country_excluded: value,
                })
              }
            }}
            onChangeIncludedState={() => {}}
            onChangeExcludedState={() => {}}
            onChangeIncludedCity={() => {}}
            onChangeExcludedCity={() => {}}
            countryNames={filterOptions.country_names}
            defaultExpanded={true}
          />

          {/* Industry Filter */}
          <IndustryFilter
            includedValues={ensureArray(filters.industry_included)}
            excludedValues={ensureArray(filters.industry_excluded)}
            industries={filterOptions.industries}
            onChangeIncluded={(value) => {
              // If clearing (empty array), clear both included and excluded
              if (value.length === 0) {
                updateFilter("industry_included", undefined)
                updateFilter("industry_excluded", undefined)
                onFiltersChange({
                  ...filters,
                  industry_included: undefined,
                  industry_excluded: undefined,
                })
              } else {
                // Remove included values from excluded list
                const currentExcluded = ensureArray(filters.industry_excluded)
                const newExcluded = currentExcluded.filter((v) => !value.includes(v))
                updateFilter("industry_excluded", newExcluded.length > 0 ? newExcluded : undefined)
                updateFilter("industry_included", value)
                onFiltersChange({
                  ...filters,
                  industry_included: value,
                  industry_excluded: newExcluded.length > 0 ? newExcluded : undefined,
                })
              }
            }}
            onChangeExcluded={(value) => {
              // If clearing (empty array), clear both included and excluded
              if (value.length === 0) {
                updateFilter("industry_included", undefined)
                updateFilter("industry_excluded", undefined)
                onFiltersChange({
                  ...filters,
                  industry_included: undefined,
                  industry_excluded: undefined,
                })
              } else {
                // Remove excluded values from included list
                const currentIncluded = ensureArray(filters.industry_included)
                const newIncluded = currentIncluded.filter((v) => !value.includes(v))
                updateFilter("industry_included", newIncluded.length > 0 ? newIncluded : undefined)
                updateFilter("industry_excluded", value)
                onFiltersChange({
                  ...filters,
                  industry_included: newIncluded.length > 0 ? newIncluded : undefined,
                  industry_excluded: value,
                })
              }
            }}
          />

          {/* Company Size Filter */}
          <CompanySizeFilter
            value={ensureArray(filters.company_size)}
            sizes={filterOptions.company_sizes}
            onChange={(value) => {
              updateFilter("company_size", value)
              onFiltersChange({ ...filters, company_size: value })
            }}
          />

          {/* Company Type Filter */}
          <CompanyTypeFilter
            includedValues={ensureArray(filters.company_type_included)}
            excludedValues={ensureArray(filters.company_type_excluded)}
            companyTypes={filterOptions.company_types.map((type) => ({
              value: type,
              label: type,
            }))}
            onChangeIncluded={(value) => {
              // If clearing (empty array), clear both included and excluded
              if (value.length === 0) {
                updateFilter("company_type_included", undefined)
                updateFilter("company_type_excluded", undefined)
                onFiltersChange({
                  ...filters,
                  company_type_included: undefined,
                  company_type_excluded: undefined,
                })
              } else {
                // Remove included values from excluded list
                const currentExcluded = ensureArray(filters.company_type_excluded)
                const newExcluded = currentExcluded.filter((v) => !value.includes(v))
                updateFilter("company_type_excluded", newExcluded.length > 0 ? newExcluded : undefined)
                updateFilter("company_type_included", value)
                onFiltersChange({
                  ...filters,
                  company_type_included: value,
                  company_type_excluded: newExcluded.length > 0 ? newExcluded : undefined,
                })
              }
            }}
            onChangeExcluded={(value) => {
              // If clearing (empty array), clear both included and excluded
              if (value.length === 0) {
                updateFilter("company_type_included", undefined)
                updateFilter("company_type_excluded", undefined)
                onFiltersChange({
                  ...filters,
                  company_type_included: undefined,
                  company_type_excluded: undefined,
                })
              } else {
                // Remove excluded values from included list
                const currentIncluded = ensureArray(filters.company_type_included)
                const newIncluded = currentIncluded.filter((v) => !value.includes(v))
                updateFilter("company_type_included", newIncluded.length > 0 ? newIncluded : undefined)
                updateFilter("company_type_excluded", value)
                onFiltersChange({
                  ...filters,
                  company_type_included: newIncluded.length > 0 ? newIncluded : undefined,
                  company_type_excluded: value,
                })
              }
            }}
          />

          {/* Keywords Filter */}
          <KeywordsFilter
            includedValues={ensureArray(filters.keywords_included)}
            excludedValues={ensureArray(filters.keywords_excluded)}
            operator="and"
            onChangeIncluded={(value) => {
              // If clearing (empty array), clear both included and excluded
              if (value.length === 0) {
                updateFilter("keywords_included", undefined)
                updateFilter("keywords_excluded", undefined)
                onFiltersChange({
                  ...filters,
                  keywords_included: undefined,
                  keywords_excluded: undefined,
                })
              } else {
                // Remove included values from excluded list
                const currentExcluded = ensureArray(filters.keywords_excluded)
                const newExcluded = currentExcluded.filter((v) => !value.includes(v))
                updateFilter("keywords_excluded", newExcluded.length > 0 ? newExcluded : undefined)
                updateFilter("keywords_included", value)
                onFiltersChange({
                  ...filters,
                  keywords_included: value,
                  keywords_excluded: newExcluded.length > 0 ? newExcluded : undefined,
                })
              }
            }}
            onChangeExcluded={(value) => {
              // If clearing (empty array), clear both included and excluded
              if (value.length === 0) {
                updateFilter("keywords_included", undefined)
                updateFilter("keywords_excluded", undefined)
                onFiltersChange({
                  ...filters,
                  keywords_included: undefined,
                  keywords_excluded: undefined,
                })
              } else {
                // Remove excluded values from included list
                const currentIncluded = ensureArray(filters.keywords_included)
                const newIncluded = currentIncluded.filter((v) => !value.includes(v))
                updateFilter("keywords_included", newIncluded.length > 0 ? newIncluded : undefined)
                updateFilter("keywords_excluded", value)
                onFiltersChange({
                  ...filters,
                  keywords_included: newIncluded.length > 0 ? newIncluded : undefined,
                  keywords_excluded: value,
                })
              }
            }}
            onChangeOperator={() => {}}
          />
        </div>
      </div>
    </div>
  )
}

